import React, { useRef, ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../models/stores/Root';
import Header from '../../components/framework/Header';
import Footer from '../../components/framework/Footer';
import CheckoutPageContainer from '../../components/framework/CheckoutPageContainer';
import CartSummarySection from '../../components/framework/CartSummarySection';
import Divider from '../../components/content/Divider';
import EditPaymentView from '../views/EditPaymentView';
import WindowService from '../../services/WindowService';
import AmazonPayView, { AmazonOrderReference } from '../views/AmazonPayView';

const AmazonPayPage: React.FC = observer(() => {
  const rootStore = useMst();
  const { configuration, customer, cart, currentStep } = rootStore;
  const checkoutDivRef = useRef<HTMLDivElement>(null);

  const scrollTopOfPage = (): void => {
    if (checkoutDivRef.current?.scrollIntoView) {
      checkoutDivRef.current.scrollIntoView();
    }
  };

  const handleLogoutClick = async (): Promise<void> => {
    await customer.logoutFlow();
  };

  const handleEditPaymentClicked = (): void => {
    rootStore.send({ type: 'EDIT_PAYMENT' });
  };

  const handleEditPaymentComplete = (): void => {
    if (cart.cartData.isAmazonPay) {
      rootStore.send({ type: 'EDIT_PAYMENT_COMPLETE' });
    } else if (rootStore.stateMatches('AmazonPay.UserStatus.Registered')) {
      rootStore.send({ type: 'AMAZONPAY_EXIT', amazonPayToken: null });
    } else {
      rootStore.send({ type: 'AMAZONPAY_EXIT', amazonPayToken: null });
    }
  };

  const handleOnOrderReferenceCreate = async (
    orderReference: AmazonOrderReference
  ): Promise<void> => {
    const orderReferenceId = orderReference.getAmazonOrderReferenceId();
    rootStore.initializeAmazonCheckout(orderReferenceId);
    await rootStore.postStripeIntentFlow();
    WindowService.removeQueryString();
  };

  const handleOnAddressSelect = async (): Promise<void> => {
    await rootStore.updateAddressFromAmazon();
    await rootStore.updateCartShippingAddressFlow();
  };

  const renderView = (): ReactElement | null => {
    if (customer.isLoading) {
      return null;
    }
    if (!!currentStep && rootStore.stateMatches('AmazonPay.Checkout.Payment')) {
      return <EditPaymentView onEditComplete={handleEditPaymentComplete} />;
    }
    return (
      <AmazonPayView
        onLogout={handleLogoutClick}
        onAddressSelect={handleOnAddressSelect}
        onOrderReferenceCreate={handleOnOrderReferenceCreate}
        onEditPayment={handleEditPaymentClicked}
        onScrollTopOfPage={scrollTopOfPage}
      />
    );
  };

  return (
    <CheckoutPageContainer cart={<CartSummarySection />} ref={checkoutDivRef}>
      <Header
        logoText={configuration.logo.text ?? ''}
        logoPublicId={configuration.logo.publicId ?? undefined}
      />
      <Divider className="md:hidden" />
      {renderView()}
      <Footer showTermsAndConditions />
    </CheckoutPageContainer>
  );
});

export default AmazonPayPage;
